<template>
  <div id="whale-backtester-restrictions-tab">
    <helpers-section-header class="mt-0 mb-6">
      Filter out unwanted stocks picked from buy signals
    </helpers-section-header>

    <v-container fluid pa-0>
      <v-row density="compact">
        <!-- Filter by stock market cap -->
        <v-col cols="12" xl="6">
          <helpers-section-sub-header class="mb-2">
            Filter by stock market cap:
          </helpers-section-sub-header>
          <v-select
            v-model="whaleTraderCriteria.filters.market_caps"
            :items="marketCaps"
            return-object
            item-title="name"
            multiple
            density="compact"
            variant="outlined"
            chips
            closable-chips
            clearable
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <v-col v-show="$vuetify.display.xl" xl="6" />

        <!-- Optionally filter by Sector/Industry -->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-2">
            Optionally filter by sector/industry:
          </helpers-section-sub-header>
          <v-treeview
            v-model:selected="whaleTraderCriteria.filters.industries"
            :items="sectors"
            open-on-click
            item-value="id"
            transition
            :return-object="false"
            select-strategy="classic"
            hoverable
            item-title="name"
            selectable
            density="compact"
          />
        </v-col>

        <v-col cols="12" xl="6">
          <helpers-section-sub-header class="mb-2">
            Countries to exclude:
          </helpers-section-sub-header>
          <v-select
            v-model="whaleTraderCriteria.filters.countries"
            :items="stock_countries"
            multiple
            density="compact"
            variant="outlined"
            chips
            closable-chips
            clearable
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <v-col cols="12" xl="6">
          <helpers-section-sub-header class="mb-2">
            Filter by type of security:
          </helpers-section-sub-header>
          <v-select
            v-model="whaleTraderCriteria.filters.security_codes"
            :items="security_codes"
            return-object
            item-title="name"
            multiple
            density="compact"
            variant="outlined"
            chips
            closable-chips
            clearable
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <v-col v-show="$vuetify.display.xl" xl="6" />

        <!-- ETF Filter -->
        <v-col cols="12" class="mt-4">
          <helpers-section-sub-header>ETF Filter</helpers-section-sub-header>
          <v-radio-group
            v-model="whaleTraderCriteria.filters.etf_filter"
            :row="!$vuetify.display.xs"
            hide-details
            class="mt-1"
          >
            <v-radio :value="0" label="Any" color="primary" />
            <v-radio :value="1" label="ETF Holdings only" color="primary" />
            <v-radio :value="2" label="Exclude ETFs" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- ADR Filter -->
        <v-col cols="12">
          <helpers-section-sub-header class="d-inline-flex align-center">
            ADR Filter
          </helpers-section-sub-header>
          <v-radio-group
            v-model="whaleTraderCriteria.filters.adr_filter"
            :row="!$vuetify.display.xs"
            hide-details
            class="mt-1"
          >
            <v-radio :value="0" label="Any" color="primary" />
            <v-radio :value="1" label="Foreign ADR only" color="primary" />
            <v-radio :value="2" label="Exclude ADRs" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- ECEF Filter -->
        <v-col cols="12" class="mt-4">
          <helpers-section-sub-header>Closed-End Fund (CEF) Filter</helpers-section-sub-header>
          <v-radio-group
            v-model="whaleTraderCriteria.filters.cef_filter"
            :row="!$vuetify.display.xs"
            hide-details
            class="mt-1"
          >
            <v-radio :value="0" label="Any" color="primary" />
            <v-radio :value="1" label="CEF Holdings only" color="primary" />
            <v-radio :value="2" label="Exclude CEFs" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- Avg. Stock Price Restrictions -->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-2">
            Avg. stock price restrictions:
          </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_stock_price_min"
                type="number"
                label="Min price (90-day avg)"
                hide-details
                density="compact"
                variant="outlined"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_stock_price_max"
                type="number"
                label="Max price (90-day avg)"
                hide-details
                density="compact"
                variant="outlined"
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="6" xl="8" class="pa-0" />

        <!-- NATR Restrictions -->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-2">
            Normalized ATR Restrictions:
          </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.natr_min"
                type="number"
                density="compact"
                variant="outlined"
                label="Min NATR (28-day avg)"
                hide-details
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.natr_max"
                type="number"
                density="compact"
                variant="outlined"
                label="Max NATR (28-day avg)"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="6" xl="8" class="pa-0" />
        <!-- Dividend Yield -->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-2"> Dividend Yield: </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="4">
              <v-text-field
                v-model="whaleTraderCriteria.filters.dividend_yield_min"
                type="number"
                density="compact"
                variant="outlined"
                label="Min. Dividend Yield"
                hide-details
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="9" />
          </v-row>
        </v-col>
        <v-col cols="12" md="8" xl="8" class="pa-0" />
        <!-- insider ownership-->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-2"> Insider Ownership: </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.insider_ownership_min"
                type="number"
                label="Min % ownership"
                hide-details
                density="compact"
                variant="outlined"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.insider_ownership_max"
                type="number"
                label="Max"
                density="compact"
                variant="outlined"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="6" xl="8" class="pa-0" />

        <!-- Avg. Daily Volume -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2"> Avg. daily volume: </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_daily_volume_min"
                type="number"
                label="From"
                hide-details
                density="compact"
                variant="outlined"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_daily_volume_max"
                type="number"
                label="To"
                density="compact"
                variant="outlined"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Avg. Dollar Volume Liquidity -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Avg. dollar volume liquidity:
          </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_dollar_vol_liquidity_min"
                type="number"
                label="From"
                density="compact"
                variant="outlined"
                hint="Avg $ value of volume minimum"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_dollar_vol_liquidity_max"
                type="number"
                label="To"
                density="compact"
                variant="outlined"
                hint="Avg $ value of volume maximum"
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <helpers-section-sub-header class="mb-0">
            Filter by volatility vs S&amp;P:
          </helpers-section-sub-header>

          <p>
            Only allow stocks whose volatility is a certain percentage less than or greater than the
            S&amp;P
          </p>
        </v-col>
        <v-col cols="12">
          <v-checkbox
            v-model="whaleTraderCriteria.filters.volatility_enabled"
            label="Enabled"
            hide-details
            density="compact"
            variant="outlined"
            class="shrink ma-0"
          />
        </v-col>
        <v-col cols="12">
          <v-row density="compact">
            <v-col cols="12" md="4" lg="3">
              <v-select
                v-model="whaleTraderCriteria.filters.volatility_time_period_id"
                :items="volatility_time_periods"
                density="compact"
                item-title="text"
                variant="outlined"
                :disabled="!whaleTraderCriteria.filters.volatility_enabled"
                label="Time Period Volatility"
              />
            </v-col>
            <v-col cols="12" md="4" lg="3">
              <v-select
                v-model="whaleTraderCriteria.filters.volatility_direction"
                :disabled="!whaleTraderCriteria.filters.volatility_enabled"
                :items="['>', '<']"
                density="compact"
                variant="outlined"
                label="greater than or less than the S&amp;P;"
                hint="Direction (greater than or less than) S&amp;P"
              />
            </v-col>
            <v-col cols="12" md="4" lg="3">
              <v-text-field
                v-model="whaleTraderCriteria.filters.volatility_percentage"
                :disabled="!whaleTraderCriteria.filters.volatility_enabled"
                label="% difference"
                density="compact"
                variant="outlined"
                hint="% difference between stock volatility and S&amp;P volatility"
                persistent-hint
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Limit same issuer -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header
            >Limit to one security per issuer (ie. if you already own GOOG, don't buy GOOGL as
            well):</helpers-section-sub-header
          >
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-checkbox
                v-model="whaleTraderCriteria.filters.limit_issuers"
                hide-details
                density="compact"
                variant="outlined"
                label="Check to limit"
                class="shrink mr-2"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Limit new purchase of a previously held stock -->
        <v-col v-if="!whaleTraderCriteria.general.screener" cols="12" mb-2>
          <helpers-section-sub-header
            >Limit new purchase of a previously held stock:</helpers-section-sub-header
          >
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="whaleTraderCriteria.filters.limit_new_purchases_days"
                type="number"
                suffix="days"
                density="compact"
                variant="outlined"
                hint="Number of days elapsed before backtest can repurchase previously held stock"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- minimum history -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header
            >Minimum days of trading history before allowing buy:</helpers-section-sub-header
          >
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="whaleTraderCriteria.filters.minimum_trading_history_days"
                type="number"
                suffix="days"
                density="compact"
                variant="outlined"
                hint="A stock must have been trading for this many days.  Prevents early buying of IPOs"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- minimum history -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header
            >Maximum days of trading history allowed:</helpers-section-sub-header
          >
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="whaleTraderCriteria.filters.maximum_trading_history_days"
                type="number"
                suffix="days"
                density="compact"
                variant="outlined"
                hint="A stock cannot have more than this many days of pricing history."
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <helpers-section-sub-header class="mb-2">
            Trading 52-week High or Low:
          </helpers-section-sub-header>
          <p>Enter the number of ATRs.</p>
          <p>
            If you enter 1 then only stocks within 1 ATR of the 52-week high (or low) will be
            accepted. The previous 2-week Average True Range will be used.
          </p>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <v-row no-gutters>
            <v-checkbox
              v-model="whaleTraderCriteria.filters.within_atr_high_enabled"
              hide-details
              density="compact"
              variant="outlined"
              class="shrink mr-2"
            />
            <v-text-field
              v-model="whaleTraderCriteria.filters.within_atr_high"
              :disabled="!whaleTraderCriteria.filters.within_atr_high_enabled"
              type="number"
              density="compact"
              variant="outlined"
              label="#ATR within 52-week high"
              hide-details
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <v-row no-gutters>
            <v-checkbox
              v-model="whaleTraderCriteria.filters.within_atr_low_enabled"
              hide-details
              density="compact"
              variant="outlined"
              class="shrink mr-2"
            />
            <v-text-field
              v-model="whaleTraderCriteria.filters.within_atr_low"
              :disabled="!whaleTraderCriteria.filters.within_atr_low_enabled"
              type="number"
              density="compact"
              variant="outlined"
              label="#ATR within 52-week low"
              persistent-hint
              hide-details
            />
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <!-- Optionally restrict to specific securities -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header class="mb-6">
            Optionally restrict to specific securities:
          </helpers-section-sub-header>

          <core-stock-manager
            :key="'only-stock-manager-' + current_trading_model.id"
            v-model="whaleTraderCriteria.filters.included_stocks"
            flat
            no-padding
          />
        </v-col>

        <!-- Optionally exclude specific securities -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header class="mb-6">
            Optionally exclude specific securities:
          </helpers-section-sub-header>

          <core-stock-manager
            :key="'exc-stock-manager-' + current_trading_model.id"
            v-model="whaleTraderCriteria.filters.excluded_stocks"
            flat
            no-padding
          />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
// Modules
import { mapWritableState } from 'pinia'
import { useUserStore, useAppStore, useWwStore } from '@/store'
import AccessMixin from '@/mixins/access'

export default {
  mixins: [AccessMixin],
  data() {
    return {
      marketCaps: [
        { name: 'Over $200 billion', id: 1 },
        { name: '$100 billion - $200 billion', id: 2 },
        { name: '$50 billion - $100 billion', id: 3 },
        { name: '$20 billion - $50 billion', id: 4 },
        { name: '$10 billion - $20 billion', id: 5 },
        { name: '$2 billion - $10 billion', id: 6 },
        { name: '$1 billion - $2 billion', id: 7 },
        { name: '$500 million - $1 billion', id: 8 },
        { name: '$250 million - $500 million', id: 9 },
        { name: 'Below $250 million', id: 10 }
      ],
      security_codes: [
        { name: 'Equity', code: 'EQS' },
        { name: 'ETF', code: 'ETF' },
        { name: 'Unit', code: 'UNT' },
        { name: 'ADR', code: 'DR' },
        { name: 'Closed-End-Fund', code: 'CEF' },
        { name: 'SP ETN REDEEM', code: 'SP' },
        { name: 'PFS Perpetual', code: 'PFS' },
        { name: 'PRF Perpetual', code: 'PRF' },
        { name: 'Systemic Transfer Plan (STP)', code: 'STP' },
        { name: 'Warrant', code: 'WAR' },
        { name: 'Tradeable Rights', code: 'TRT' },
        { name: 'Bond', code: 'BND' },
        { name: 'Contingent Value Right (CVR)', code: 'CVR' },
        { name: 'When Issued (WI)', code: 'WIS' },
        { name: 'Unknown', code: null },
        { name: 'Redeemable Shares', code: 'RDS' },
        { name: 'Chess Depository Interest', code: 'CDI' },
        { name: 'Exchange Traded Commodity (ETC)', code: 'ETC' },
        //{name:'Mutual Fund', code:'MF'},
        { name: 'Letter of Allotment', code: 'LOA' },
        { name: 'Income Fund', code: 'IF' },
        { name: 'Redemption Rights', code: 'RDR' },
        { name: 'Subscription Receipts', code: 'SRT' }
      ]
    }
  },
  computed: {
    ...mapWritableState(useAppStore, [
      'whaleTraderCriteria',
      'current_trading_model',
      'signalView',
      'volatility_time_periods'
    ]),
    ...mapWritableState(useWwStore, ['sectors', 'stock_countries'])
    // ...mapWritableState(useAppStore, {
    //   criteria: store => store.whaleTraderCriteria.filters,
    /*criteria(store) {
        return store.whaleTraderCriteria.filters
      },*/

    // })
    // ...mapWritableState(useAppStore, {criteria: 'whaleTraderCriteria.filters'}),
  }
}
</script>
